import CallErrors from 'app/components/call/CallErrors';
import { usePageBreakpoint } from 'app/components/layout/Breakpoint';
import Page, { PAGE_WIDE_STYLE } from 'app/components/layout/Page';
import { useI18n } from 'app/components/localization';
import PageWidth from 'app/components/presentation/containers/page/PageWidth';
import DomainRedirect from 'app/components/router/DomainRedirect';
import HardRedirect from 'app/components/router/HardRedirect';
import Footer from 'app/components/teacherRecruitment/Footer';
import Header from 'app/components/teacherRecruitment/Header';
import ProgressBar from 'app/components/teacherRecruitment/ProgressBar';
import RecruitmentPhase from 'app/components/teacherRecruitment/RecruitmentPhase';
import appConfig from 'app/config/client';
import { NumericBreakpoint } from 'app/constants/Breakpoints';
import { GREY_B, GREY_V } from 'app/constants/Colors';
import { LETTER_SPACING_A } from 'app/constants/Styles';
import { isTeacher } from 'app/models/auth/selectors';
import { useClasses } from 'app/utils/css';
import React, { Fragment, FunctionComponent, ReactNode } from 'react';
import { useAuthUser } from '../auth/AuthUserContext';
import { getTutorSiteUrl, TEACHER_SITE_DOMAIN } from 'app/constants/TeacherSite';
import { ENGLISH } from 'app/constants/enums/Language';
import WithFontsForLanguage from 'app/components/layout/WithFontsForLanguage';

interface IProps {
  children: ReactNode;
  disableTeacherSignupNavigation?: boolean;
  recruitmentPhase?: RecruitmentPhase;
}
const PageWrapper: FunctionComponent<IProps> = ({
  children,
  disableTeacherSignupNavigation,
  recruitmentPhase,
}) => {
  const { t } = useI18n();
  const classes = useClasses({
    pageWrapper: {
      letterSpacing: LETTER_SPACING_A,
      flexGrow: 1,
      display: 'flex',
      color: GREY_B,
    },
    headerFooterBackground: {
      background: 'white',
      // Needed for some absolutely positioned elements in the header.
      position: 'relative',
    },
    progressBarBackground: {
      background: GREY_V,
    },
  });

  const user = useAuthUser();

  // If the user is a teacher, redirect them to the tutor site.
  if (isTeacher(user)) {
    return (<HardRedirect href={getTutorSiteUrl(appConfig.deployInfo)} />);
  }

  const contents = (
    <div className={classes.pageWrapper}>
      <Page
        header={
          <SizeWrapperClassName>{(sizeWrapperClassName) =>
            <Fragment>
              <div className={classes.headerFooterBackground}>
                <PageWidth maxWidth="1200px">
                  <div className={sizeWrapperClassName}>
                    <Header disableTeacherSignupNavigation={disableTeacherSignupNavigation} />
                  </div>
                </PageWidth>
              </div>

              {recruitmentPhase !== undefined &&
                <div className={classes.progressBarBackground}>
                  <PageWidth maxWidth="1200px">
                    <div className={sizeWrapperClassName}>
                      <ProgressBar recruitmentPhase={recruitmentPhase} />
                      <CallErrors />
                    </div>
                  </PageWidth>
                </div>}
            </Fragment>
          }</SizeWrapperClassName>
        }
        footer={
          <SizeWrapperClassName>{(sizeWrapperClassName) =>
            <div className={classes.headerFooterBackground}>
              <PageWidth maxWidth="1200px">
                <div className={sizeWrapperClassName}>
                  <Footer />
                </div>
              </PageWidth>
            </div>
          }</SizeWrapperClassName>
        }
        variant={PAGE_WIDE_STYLE}
        title={t('page.teach.meta.title')}
        description={t('page.teach.meta.description')}
      >
        {/* The teacher recruitment page is currently only displayed in English, but that may change in the future */}
        <WithFontsForLanguage languageCode={ENGLISH.id} wrap="div">
          {children}
        </WithFontsForLanguage>
      </Page>
    </div>
  );

  return (
    appConfig.environment.teachDomainRedirect ?
      <DomainRedirect domain={TEACHER_SITE_DOMAIN}>
        {contents}
      </DomainRedirect>
      : contents
  );
};

// Needs to be nested inside <Page> in order to use the correct breakpoint
const SizeWrapperClassName: React.FunctionComponent<{
  children: (sizeWrapperClassName: string) => React.ReactNode,
}> = ({ children }) => {
  const isWide = usePageBreakpoint() >= NumericBreakpoint.Wide;
  const classes = useClasses({
    sizeWrapper: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      width: '100%',
      padding: isWide ? '0 1em' : '0 5px',
    },
  });
  return children(classes.sizeWrapper) as React.ReactElement<any>;
};

export default PageWrapper;
